// Third-party libraries
import React, { useEffect, useContext, useState, useRef } from "react";
import { Link as DomLink } from "react-router-dom";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import parse from "html-react-parser";
import InfoIcon from "@mui/icons-material/Info";
import ReactMarkdown from "react-markdown";
import {
  Select,
  Container,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Grid,
  Card,
  Stack,
  Backdrop,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  MenuItem,
  InputLabel,
} from "@mui/material";

// Local modules
import { CartContext } from "../../utils/stateHandlers/contexts";
import { api, storage, constants } from "../../utils";
import { functions } from "../../utils";
import formatting from "../../utils/formatHelpers";
import { Mobile, Default } from "../../utils/deviceHelpers";
import gaFunctions from "../../utils/GAComponents/GAFunctions";

// MINIMAL CHANGE: Updated iframe container box to fill container properly
const iFrameBoxStyles = {
  width: "100%",
  height: "40px",
  minHeight: "40px",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  padding: "8px 14px",
  boxSizing: "border-box",
  backgroundColor: "#fff",
};

const inputBoxStyles = {
  width: "100%",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  padding: "10px 14px",
  boxSizing: "border-box",
};

const inputLabelStyles = {
  fontSize: "1.2rem",
};

export default function Checkout(props) {
  const { cartState, cartDispatch } = useContext(CartContext);
  const {
    cart,
    fields,
    order,
    totals,
    submitted,
    shippingAddress,
    placedAt,
    pageLoaded,
    newAddress,
    newCreditCard,
    specialInstructions,
  } = cartState;

  const [openNewAddressModal, setOpenNewAddressModal] = useState(false);
  const [openNewCardModal, setOpenNewCardModal] = useState(false);
  const [shipTo, setShipTo] = useState("");
  const [shipVia, setShipVia] = useState("");
  const [showShippingPrice, setShowShippingPrice] = useState(true);
  const [customerNo, setCustomerNo] = useState("");
  const [isCheckoutValid, setIsCheckoutValid] = useState(false);

  // MINIMAL CHANGE: Reference to hold the active SpreedlyHostedFields SDK instance
  const spreedlySdkRef = useRef(null);
  const spreedlyInitRef = useRef(false);
  const spreedlyRequestRef = useRef(0);
  const spreedlySubmittingRef = useRef(false);

  // Safely extract cartId from storage
  const rawCartId = storage.getStorageItem("cartId");
  const cartId =
    rawCartId && rawCartId !== "null" && rawCartId !== "undefined"
      ? rawCartId
      : null;

  const currentUser = JSON.parse(storage.getStorageItem("currentUser") || "{}");

  useEffect(() => {
    cartDispatch({ type: "setPageLoaded", payload: false });
    cartDispatch({
      type: "setCheckout",
      payload: {
        fields: [],
        order: null,
        totals: null,
      },
    });
    cartDispatch({
      type: "setOrderSubmitted",
      payload: {
        placedAt: "",
      },
    });

    let isMounted = true;

    const body = {
      cart_id: cartId,
      ShipTo: null,
      TermsCode: null,
      ShipVia: null,
    };

    api.postRequest("orders/form", body).then(async (response) => {
      if (!isMounted) return;
      if (response.isAxiosError) {
        cartDispatch({ type: "setPageLoaded", payload: true });
        return;
      }

      response.data.totals.trans = 0;
      const orderData = response.data.order;
      const totalsData = response.data.totals;
      const baseFields = response.data.fields || [];

      const shipToField = baseFields.find((field) => field.name === "ShipTo");
      const defaultShipToValue = shipToField?.default;

      setCustomerNo(response.data.customerNo);

      const customerPayload = {
        customer: { customerNo: currentUser?.CustomerNo },
      };

      try {
        const paymentCards = await handleGetPayments(customerPayload);
        const updatedFieldsStep1 = updateFields(baseFields);
        const termsUpdatedFields = updateTermsCodeValues(
          updatedFieldsStep1,
          paymentCards || []
        );

        cartDispatch({
          type: "setCheckout",
          payload: {
            fields: sortFields(termsUpdatedFields),
            order: orderData,
            totals: totalsData,
          },
        });

        // Automatically update state with the default ShipTo value retrieved from backend
        if (defaultShipToValue) {
          cartDispatch({
            type: "updateFields",
            field: "ShipTo",
            payload: defaultShipToValue,
          });
        }

        cartDispatch({ type: "setPageLoaded", payload: true });

        const gaCheckoutInfo = {
          items: cart,
          value: totalsData.total,
        };
        gaFunctions.gaBeginCheckout(gaCheckoutInfo);
      } catch (error) {
        console.error("Initial load error:", error);
        toast.error(
          "An unexpected error occurred during checkout initialization."
        );
        cartDispatch({ type: "setPageLoaded", payload: true });
      }
    });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validateCheckoutFields() {
    if (order != null) {
      if (!order.ShipTo || !order.ShipVia || !order.TermsCode) {
        return false;
      }
      return true;
    }
    return false;
  }

  const handleShipViaChange = (e, field) => {
    const value = e.target.value;
    setShipVia(value);
    cartDispatch({
      type: "updateFields",
      field: field.name,
      payload: value,
    });
    reloadFields(value);
  };

  const handleTermsChange = (e) => {
    cartDispatch({
      type: "setNewCreditCard",
      field: "Name",
      payload: e.target.value,
    });
  };

  const handleSelectChange = (e, field) => {
    const value = e.target.value;

    setShipTo(value);

    cartDispatch({
      type: "updateFields",
      field: field.name,
      payload: value,
    });

    if (field.name === "ShipTo") {
      cartDispatch({
        type: "updateFields",
        field: "ShipVia",
        payload: null,
      });

      reloadFields(null, value);
    }
  };

  const SelectField = (field) => {
    return (
      <Stack>
        <div>
          <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
            {field.label} *
            {field.name === "ShipTo" && (
              <span
                onClick={() => setOpenNewAddressModal(true)}
                className="header-link pointer"
                style={{ marginBottom: ".3rem", padding: "unset" }}
              >
                Add New Address
              </span>
            )}
            {field.name === "TermsCode" && (
              <span
                onClick={() => setOpenNewCardModal(true)}
                className="header-link pointer"
                style={{ marginBottom: ".3rem", padding: "unset" }}
              >
                Add New Credit Card
              </span>
            )}
          </Typography>
        </div>
        <Select
          key={field.name}
          defaultValue={field.default}
          size="small"
          onChange={(e) => handleSelectChange(e, field)}
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1px solid #939598",
                borderRadius: "0",
              },
            },
          }}
        >
          {(stripSelectLabels(field) || []).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  const InputField = (field) => (
    <Stack>
      <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
        {field.label}
      </Typography>
      <TextField
        color="primary"
        onChange={(e) => {
          cartDispatch({
            type: "updateFields",
            field: field.name,
            payload: e.target.value,
          });
        }}
        size="small"
        sx={{
          width: "100%",
          margin: "unset",
        }}
      />
    </Stack>
  );

  const ShipViaField = (field) => {
    return (
      <FormControl>
        <FormLabel>
          <Typography
            sx={{ fontWeight: "700", paddingBottom: ".3rem", color: "black" }}
          >
            {field.label} *
          </Typography>
        </FormLabel>
        <RadioGroup
          value={order?.ShipVia || ""}
          sx={{ marginLeft: "2rem" }}
          onChange={(e) => handleShipViaChange(e, field)}
        >
          {(field.values || []).map((shipOption) => (
            <FormControlLabel
              key={shipOption.desc}
              value={JSON.stringify({
                amount: shipOption.amount,
                code: shipOption.code,
                desc: shipOption.desc,
              })}
              control={<Radio disabled={!showShippingPrice} />}
              label={
                <Typography>
                  {shipOption.desc} | {formatting["price2"](shipOption.amount)}
                </Typography>
              }
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  };

  const SpecialInstructions = (field) => (
    <Stack>
      <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
        Special Instructions
      </Typography>
      <TextField
        color="primary"
        variant="outlined"
        multiline
        fullWidth
        rows={3}
        sx={{ margin: "unset" }}
        onChange={(e) => {
          cartDispatch({
            type: "setSpecialInstructions",
            payload: e.target.value,
          });
        }}
      />
    </Stack>
  );

  function stripSelectLabels(field) {
    if (!field || !field.values) return [];
    return field.values.map((option) => {
      let label = option.label || "";
      label = label.replace(" <br />", " | ");
      label = label.replace(" <br />", " | ");
      return { ...option, label };
    });
  }

  async function checkout() {
    try {
      const shippingAddressField = (fields || []).find(
        (field) => field.name === "ShipTo"
      );

      const selectedAddress = (shippingAddressField?.values || []).find(
        (address) => address.value === order?.ShipTo
      );

      cartDispatch({
        type: "setShippingAddress",
        payload: selectedAddress?.label || "",
      });

      const shipViaParsed = order?.ShipVia ? JSON.parse(order.ShipVia) : {};
      const orderInfo = {
        cart_id: cartId,
        MultipleOk: order?.MultipleOk ? "Y" : "",
        Notes: specialInstructions,
        PoNumber: order?.PoNumber,
        ShipTo: order?.ShipTo,
        ShipVia: `${shipViaParsed.desc || ""} | ${shipViaParsed.amount || ""}`,
        TermsCode: order?.TermsCode,
      };

      cartDispatch({ type: "setPageLoaded", payload: false });

      const response = await api.postRequest("orders/submit", orderInfo);

      cartDispatch({
        type: "setOrderSubmitted",
        payload: {
          placedAt: response.data.PlacedAt,
          submitted: true,
        },
      });
      cartDispatch({ type: "setPageLoaded", payload: true });
      cartDispatch({ type: "setCart", payload: [] });
      cartDispatch({ type: "setCartCount", payload: 0 });

      const gaPurchaseInfo = {
        items: cart,
        transaction_id: cartId,
        tax: totals?.tax?.amount,
        shipping: totals?.trans,
        total: totals?.total,
      };
      gaFunctions.gaPurchase(gaPurchaseInfo);
    } catch (error) {
      cartDispatch({ type: "setPageLoaded", payload: true });

      if (isAxiosError(error)) {
        const status = error.response?.status;
        const data = error.response?.data;

        if (status === 400 && data?.errors) {
          const messages = Object.values(data.errors).join(", ");
          toast.error(
            `There was an error processing your request. ${messages}`
          );
        } else {
          toast.error(
            `There was an error processing your request. ${
              data?.message || error.message
            }`
          );
        }
      } else {
        toast.error(`Unexpected error: ${error.message || error}`);
      }

      console.error("Checkout error:", error);
    }
  }

  function sortFields(fieldsList) {
    const sortedFields = fieldsList.map((field) => ({
      ...field,
      SortOrder:
        field.name === "ShipTo"
          ? 1
          : field.name === "MultipleOk"
          ? 2 // Shipping Instructions dropdown
          : field.name === "ShipVia"
          ? 3
          : field.name === "TermsCode"
          ? 4
          : field.name === "PoNumber"
          ? 5
          : field.name === "Notes"
          ? 6
          : 7,
    }));
    return sortedFields.sort((a, b) => a.SortOrder - b.SortOrder);
  }

  function updateFields(newFields) {
    const preservedFields = fields
      ? fields.filter((field) =>
          ["MultipleOk", "PoNumber", "Notes"].includes(field.name)
        )
      : [];

    const relevantNewFields = (newFields || []).filter((field) =>
      [
        "ShipTo",
        "ShipVia",
        "TermsCode",
        "MultipleOk",
        "PoNumber",
        "Notes",
      ].includes(field.name)
    );

    const combinedMap = new Map();
    [...preservedFields, ...relevantNewFields].forEach((field) => {
      combinedMap.set(field.name, field);
    });

    return sortFields(Array.from(combinedMap.values()));
  }

  async function reloadFields(
    selectedShipVia = undefined,
    selectedShipTo = undefined
  ) {
    if (!cartId) {
      console.warn("No valid cartId found in storage.");
      cartDispatch({ type: "setPageLoaded", payload: true });
      return;
    }

    // Use explicitly provided ShipTo when available.
    // Otherwise, fall back to existing checkout state.
    const shipToValue =
      selectedShipTo !== undefined
        ? selectedShipTo
        : shipTo || order?.ShipTo || null;

    const selectedTermsCode = order?.TermsCode || null;

    // Explicit null means ShipVia is intentionally being reset.
    // Undefined means use the existing checkout value.
    const shipViaValue =
      selectedShipVia !== undefined ? selectedShipVia : order?.ShipVia;

    let shipViaParsed = null;

    if (shipViaValue) {
      try {
        shipViaParsed =
          typeof shipViaValue === "string"
            ? JSON.parse(shipViaValue)
            : shipViaValue;
      } catch (e) {
        console.warn("Could not parse ShipVia JSON:", e);
      }
    }

    setShowShippingPrice(false);

    const body = {
      cart_id: cartId,
      ShipTo: shipToValue,
      TermsCode: selectedTermsCode,
      ShipVia: shipViaParsed,
    };

    try {
      const response = await api.postRequest("orders/form", body);

      if (response.isAxiosError) {
        return;
      }

      const baseFields = response.data.fields || [];
      const totalsData = response.data.totals;

      // Reset shipping amount when the shipping address changes
      // and the shipping method is intentionally cleared.
      if (selectedShipTo !== undefined && selectedShipVia === null) {
        totalsData.trans = 0;
      }

      const updatedFieldsStep1 = updateFields(baseFields);

      setCustomerNo(response.data.customerNo);

      const customerData = {
        customer: {
          customerNo: currentUser?.CustomerNo,
        },
      };

      const cards = await handleGetPayments(customerData);

      const updatedFieldsWithTerms = updateTermsCodeValues(
        updatedFieldsStep1,
        cards || []
      );

      // Update checkout fields and totals with the latest backend response.
      cartDispatch({
        type: "reloadFields",
        payload: {
          updated_fields: updatedFieldsWithTerms,
          totals: totalsData,
        },
      });
    } catch (error) {
      console.error("reloadFields error:", error);

      toast.error("Unexpected error while reloading fields.");
    } finally {
      setShowShippingPrice(true);

      cartDispatch({
        type: "setPageLoaded",
        payload: true,
      });
    }
  }

  async function submitNewAddress() {
    const newAddressInfo = {
      name: newAddress.name,
      street_1: newAddress.street_1,
      street_2: newAddress.street_2,
      state: newAddress.state,
      city: newAddress.city,
      zip_code: newAddress.zip_code,
      country_code: newAddress.country_code,
      address_type: newAddress.address_type,
    };

    try {
      const response = await api.postRequest(
        "orders/addAddress",
        newAddressInfo
      );

      if (!response || response.isAxiosError) {
        const errorMessage =
          response?.data?.errors?.join(", ") || "Error adding address.";

        toast.error(
          `There was an error processing your request. ${errorMessage}`
        );
        return;
      }

      // Adjust this path if your API returns ShipToId elsewhere.
      const shipToId = response.data?.ShipToId ?? response.ShipToId;

      if (shipToId == null || shipToId === "") {
        console.error("Unexpected add-address response:", response);
        toast.error(
          "Address added, but the new address ID could not be retrieved."
        );
        return;
      }

      // Immediately select the new address.
      setShipTo(String(shipToId));

      cartDispatch({
        type: "updateFields",
        field: "ShipTo",
        payload: String(shipToId),
      });

      // Clear the previous shipping method because it may not apply
      // to the newly selected address.
      cartDispatch({
        type: "updateFields",
        field: "ShipVia",
        payload: null,
      });

      setOpenNewAddressModal(false);

      toast.success("Your address has been added successfully.");

      // Reload the backend fields so the ShipTo dropdown gets
      // the newly created address in its options.
      await reloadFields(null, String(shipToId));
    } catch (error) {
      console.error("Error adding shipping address:", error);

      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred while adding the address."
      );
    }
  }

  function submitNewCard() {
    const sdk = spreedlySdkRef.current;

    if (!sdk || !spreedlyInitRef.current) {
      toast.error("Secure payment form is not ready. Please try again.");
      return;
    }

    if (spreedlySubmittingRef.current) {
      return;
    }

    const name = (newCreditCard?.Name || "").trim();
    const month = String(newCreditCard?.ExpirationMonth || "").trim();
    const year = String(newCreditCard?.ExpirationYear || "").trim();

    if (!name) {
      toast.error("Please enter the name on the card.");
      return;
    }

    if (!/^(0?[1-9]|1[0-2])$/.test(month)) {
      toast.error("Please enter a valid expiration month.");
      return;
    }

    if (!/^\d{4}$/.test(year)) {
      toast.error("Please enter a valid four-digit expiration year.");
      return;
    }

    const nameParts = name.split(/\s+/);

    const formData = {
      first_name: nameParts[0] || "",
      last_name: nameParts.slice(1).join(" ") || "",
      month: month.padStart(2, "0"),
      year,
    };

    try {
      spreedlySubmittingRef.current = true;
      sdk.submit(formData);
    } catch (error) {
      spreedlySubmittingRef.current = false;
      console.error("Spreedly submission error:", error);
      toast.error("Unable to submit payment details. Please try again.");
    }
  }

  async function handleGetPayments(data) {
    try {
      const response = await api.postRequest("credit/get-payment", data);
      const res = response.data;
      if (response.isAxiosError) {
        toast.error(
          "There was an error processing your request. Failed to get payment."
        );
        return [];
      } else {
        return res;
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.title ||
        "Failed to get payment.";
      toast.error(`There was an error processing your request. ${message}`);
      return [];
    }
  }

  function updateTermsCodeValues(formFields, cards) {
    return (formFields || []).map((field) => {
      if (field.name === "TermsCode") {
        const cardOptions = (cards || []).map((card) => ({
          label: card.cardNoMasked,
          value: `${card.cardNoMasked}|${card.expiration}`,
        }));

        return {
          ...field,
          values: [...cardOptions, ...(field.values || [])],
        };
      }
      return field;
    });
  }

  async function handleVerifyAndAddPayment(data) {
    try {
      const response = await api.postRequest("credit/verify-credit", data);

      if (!response || response.isAxiosError) {
        toast.error(
          "There was an error processing your payment. Please try again."
        );
        return;
      }

      const result = response.data;

      if (!result?.response?.success) {
        toast.error(
          result?.response?.message ||
            "Card verification failed. Please try another card."
        );
        return;
      }

      const payment = result.payment_method;

      if (!payment?.token) {
        toast.error("The verified payment method did not include a token.");
        return;
      }

      const paymentInfo = {
        customerNo,
        cardHolderName: payment.full_name,
        cardType: payment.card_type,
        cardLast4: payment.last_four_digits,
        expirationMMYY: functions.formatMMYY(payment.month, payment.year),
        cardToken: payment.token,
      };

      const addPaymentResponse = await api.postRequest("credit/add-payment", {
        payment: paymentInfo,
      });

      if (!addPaymentResponse || addPaymentResponse.isAxiosError) {
        const errors = addPaymentResponse?.data?.errors;

        const errorMessage = Array.isArray(errors)
          ? errors.join(", ")
          : "Unable to save the payment method.";

        toast.error(errorMessage);
        return;
      }

      toast.success("Payment added successfully.");

      spreedlySubmittingRef.current = false;
      setOpenNewCardModal(false);

      await reloadFields();
    } catch (error) {
      console.error("Payment verification or saving failed:", error);

      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred while adding your card."
      );
    } finally {
      spreedlySubmittingRef.current = false;
    }
  }

  useEffect(() => {
    setIsCheckoutValid(validateCheckoutFields());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  useEffect(() => {
    if (!openNewCardModal) {
      return undefined;
    }

    let isMounted = true;
    let sdk = null;

    const requestId = ++spreedlyRequestRef.current;

    spreedlyInitRef.current = false;
    spreedlySubmittingRef.current = false;

    const initializeSpreedly = async () => {
      try {
        const authResponse = await api.getRequest("credit/spreedly-auth");

        if (!isMounted || requestId !== spreedlyRequestRef.current) {
          return;
        }

        if (!authResponse || authResponse.isAxiosError || !authResponse.data) {
          throw new Error("Unable to retrieve secure payment configuration.");
        }

        if (typeof window === "undefined" || !window.SpreedlyHostedFields) {
          throw new Error("Spreedly Hosted Fields SDK is not loaded.");
        }

        sdk = new window.SpreedlyHostedFields(authResponse.data);
        spreedlySdkRef.current = sdk;

        sdk.on("ready", () => {
          if (!isMounted || requestId !== spreedlyRequestRef.current) {
            return;
          }

          try {
            sdk.setStyles("number", {
              width: "100%",
              height: "100%",
              fontSize: "1rem",
            });

            sdk.setStyles("cvv", {
              width: "100%",
              height: "100%",
              fontSize: "1rem",
            });

            spreedlyInitRef.current = true;
          } catch (error) {
            console.error("Unable to style Spreedly fields:", error);
            toast.error("Unable to initialize secure payment fields.");
          }
        });

        sdk.on("error", (error) => {
          if (!isMounted || requestId !== spreedlyRequestRef.current) {
            return;
          }

          spreedlySubmittingRef.current = false;

          console.error("Spreedly Hosted Fields error:", error);

          const message =
            typeof error === "string"
              ? error
              : error?.message || "Payment validation failed.";

          toast.error(`Payment failed: ${message}`);
        });

        sdk.on("tokenGenerated", async (response) => {
          if (!isMounted || requestId !== spreedlyRequestRef.current) {
            return;
          }

          spreedlySubmittingRef.current = false;

          const paymentMethod = response?.tokenResponse?.payment_method;
          const paymentMethodToken = paymentMethod?.token;

          if (!paymentMethodToken) {
            console.error("Unexpected Spreedly token response:", response);

            toast.error(
              "Unable to tokenize payment details. Please try again."
            );
            return;
          }

          const verifyData = {
            transaction: {
              payment_method_token: paymentMethodToken,
              retain_on_success: true,
            },
          };

          await handleVerifyAndAddPayment(verifyData);
        });

        sdk.inAppElements({
          number: {
            containerId: "spreedly-number-test",
          },
          cvv: {
            containerId: "spreedly-cvv-test",
          },
        });
      } catch (error) {
        if (!isMounted || requestId !== spreedlyRequestRef.current) {
          return;
        }

        console.error("Spreedly initialization failed:", error);
        toast.error(
          error?.message || "Could not initialize secure payment form."
        );
      }
    };

    initializeSpreedly();

    return () => {
      isMounted = false;

      if (spreedlyRequestRef.current === requestId) {
        spreedlyRequestRef.current += 1;
      }

      spreedlyInitRef.current = false;
      spreedlySubmittingRef.current = false;

      if (spreedlySdkRef.current === sdk) {
        spreedlySdkRef.current = null;
      }

      if (sdk) {
        try {
          if (typeof sdk.destroy === "function") {
            sdk.destroy();
          }
        } catch (error) {
          console.error("Error cleaning up Spreedly SDK:", error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNewCardModal]);

  return (
    <CartContext.Provider value={{ cartState, cartDispatch }}>
      <Container maxWidth="xl">
        <Mobile>
          <Stack spacing={2}>
            <Grid item xs={6}>
              <Card
                variant="outlined"
                sx={{
                  marginTop: "1.6rem",
                  padding: "2rem",
                }}
              >
                {!submitted ? (
                  <Stack spacing={2}>
                    <Typography variant="h1" sx={{ margin: "unset" }}>
                      Place Your Order
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      align="center"
                      sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                    ></Typography>
                    {(fields || []).map((field) => (
                      <React.Fragment key={field.name}>
                        {field.name === "ShipTo"
                          ? SelectField(field)
                          : field.name === "ShipVia"
                          ? ShipViaField(field)
                          : field.name === "MultipleOk"
                          ? SelectField(field) // Renders "Shipping Instructions"
                          : field.name === "TermsCode"
                          ? SelectField(field)
                          : field.name === "PoNumber"
                          ? InputField(field) // Renders "Po #"
                          : field.name === "Notes"
                          ? SpecialInstructions(field) // Renders "Special Instructions"
                          : null}
                      </React.Fragment>
                    ))}
                  </Stack>
                ) : (
                  <Stack spacing={3}>
                    <Typography variant="h1" sx={{ margin: "unset" }}>
                      Your Order Has Been Submitted!
                    </Typography>
                    <Typography>
                      Please check your email for confirmation.
                    </Typography>
                    <Typography>Order Placed At: {placedAt}</Typography>
                    <Typography>
                      Billing To: {currentUser?.BillTo?.Street1} |{" "}
                      {currentUser?.BillTo?.Street2} |{" "}
                      {currentUser?.BillTo?.City}, {currentUser?.BillTo?.State}{" "}
                      {currentUser?.BillTo?.ZipCode}
                    </Typography>
                    <Typography>Shipping To: {shippingAddress}</Typography>
                  </Stack>
                )}
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card
                variant="outlined"
                sx={{ marginTop: "1.6rem", padding: "2rem" }}
              >
                <Stack spacing={2}>
                  <Typography variant="h1" sx={{ margin: "unset" }}>
                    Order Details
                  </Typography>
                  <Typography
                    variant="greenRectangle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <InfoIcon /> Prices and weights shown are estimates and
                    subject to change. Most product is sold by weight and billed
                    at daily market prices. Orders will be priced at the metal
                    markets on the date of receipt, if order is shipped in the
                    same day; or the markets of the day after the order is
                    received for out of stock product. Market price is the
                    second London fix plus a surcharge.
                  </Typography>
                  <Typography
                    variant="articleSubTitle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <ReactMarkdown linkTarget="_blank">
                      If you currently pay by Credit Card, you will now see a
                      2.9% Credit Card fee on your invoice. If you would like to
                      avoid this fee, [**CLICK
                      HERE**](https://api2.hooverandstrong.com/pdfs/CreditApplication-2-7-24-Fillable.pdf)
                      to fill out a Net 30 account credit application.
                    </ReactMarkdown>
                  </Typography>
                  <Typography
                    variant="articleSubTitle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <ReactMarkdown linkTarget="_blank">
                      Please note certified diamonds and harmony melee
                      availability is not confirmed until your order is
                      processed and you receive a sales order confirmation
                    </ReactMarkdown>
                  </Typography>
                  <Stack spacing={3}>
                    {totals && (
                      <>
                        {(cart || []).map((lineItem) => (
                          <React.Fragment key={lineItem.id}>
                            {(lineItem.invoices || []).map((invoice) => (
                              <Stack key={invoice.specification}>
                                <Typography
                                  variant="articleText"
                                  sx={{ marginBottom: "1rem" }}
                                >
                                  <strong>
                                    {parse(invoice.specification)}
                                  </strong>{" "}
                                  | {formatting["removeSpace"](invoice.weight)}{" "}
                                  | {formatting["price2"](invoice.total)}
                                  {lineItem.material === "D" &&
                                  lineItem.finger_size !== "" ? (
                                    <>
                                      <br />
                                      SKU {lineItem.finger_size}
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Typography>
                                <Typography variant="subtitle2">
                                  Estimated Ship Date:{" "}
                                  {lineItem.ship_info.ship_date}
                                </Typography>
                              </Stack>
                            ))}
                          </React.Fragment>
                        ))}

                        <Divider flexItem />
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Subtotal: {formatting["price2"](totals.subtotal)}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Shipping:
                          {showShippingPrice ? (
                            formatting["price2"](totals.trans)
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Sales Tax:
                          {showShippingPrice ? (
                            formatting["price2"](
                              totals.tax && totals.tax.amount
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          <strong>
                            Total:{" "}
                            {showShippingPrice ? (
                              formatting["price2"](totals.total)
                            ) : (
                              <Skeleton
                                variant="text"
                                width="10rem"
                                className="float-right"
                              />
                            )}
                          </strong>
                        </Typography>
                      </>
                    )}
                  </Stack>
                  {!submitted && (
                    <Stack
                      direction="row"
                      spacing={3}
                      justifyContent="flex-end"
                      alignItems="flex-end"
                      sx={{ marginTop: "2rem" }}
                    >
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        onClick={() => checkout()}
                        disabled={!isCheckoutValid}
                      >
                        Place Order
                      </Button>
                      <Button
                        size="small"
                        color="secondary"
                        variant="contained"
                      >
                        <DomLink to={{ pathname: "/cart" }}>Cancel</DomLink>
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Stack>
        </Mobile>
        <Default>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Card
                variant="outlined"
                sx={{
                  marginTop: "1.6rem",
                  padding: "3rem",
                  minHeight: submitted ? "" : "100rem",
                }}
              >
                {!submitted ? (
                  <Stack spacing={2}>
                    <Typography variant="h1" sx={{ margin: "unset" }}>
                      Place Your Order
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      align="center"
                      sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                    ></Typography>
                    {!pageLoaded ? (
                      <Stack spacing={1} sx={{ width: "100%" }}>
                        <Skeleton animation="wave" height={40} />
                        <Skeleton animation="wave" height={40} />
                        <Skeleton animation="wave" height={40} />
                        <Skeleton animation="wave" height={40} />
                        <Skeleton animation="wave" height={40} />
                      </Stack>
                    ) : (
                      <>
                        {(fields || []).map((field) => (
                          <React.Fragment key={field.name}>
                            {field.name === "ShipTo"
                              ? SelectField(field)
                              : field.name === "ShipVia"
                              ? ShipViaField(field)
                              : field.name === "MultipleOk"
                              ? SelectField(field)
                              : field.name === "TermsCode"
                              ? SelectField(field)
                              : field.name === "PoNumber"
                              ? InputField(field)
                              : field.name === "Notes"
                              ? SpecialInstructions(field)
                              : null}
                          </React.Fragment>
                        ))}
                        <Typography variant="subtitle2">
                          * = required
                        </Typography>
                      </>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={3}>
                    <Typography variant="h1" sx={{ margin: "unset" }}>
                      Your Order Has Been Submitted!
                    </Typography>
                    <Typography>
                      Please check your email for confirmation.
                    </Typography>
                    <Typography>Order Placed At: {placedAt}</Typography>
                    <Typography>
                      Billing To: {currentUser?.BillTo?.Street1} |{" "}
                      {currentUser?.BillTo?.Street2} |{" "}
                      {currentUser?.BillTo?.City}, {currentUser?.BillTo?.State}{" "}
                      {currentUser?.BillTo?.ZipCode}
                    </Typography>
                    <Typography>Shipping To: {shippingAddress}</Typography>
                  </Stack>
                )}
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card
                variant="outlined"
                sx={{ marginTop: "1.6rem", padding: "3rem" }}
              >
                <Stack spacing={2}>
                  <Typography variant="h1" sx={{ margin: "unset" }}>
                    Order Details
                  </Typography>
                  <Typography
                    variant="greenRectangle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <InfoIcon /> Prices and weights shown are estimates and
                    subject to change. Most product is sold by weight and billed
                    at daily market prices. Orders will be priced at the metal
                    markets on the date of receipt, if order is shipped in the
                    same day; or the markets of the day after the order is
                    received for out of stock product. Market price is the
                    second London fix plus a surcharge.
                  </Typography>
                  <Typography
                    variant="articleSubTitle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <ReactMarkdown linkTarget="_blank">
                      If you currently pay by Credit Card, you will now see a
                      2.9% Credit Card fee on your invoice. If you would like to
                      avoid this fee, [**CLICK
                      HERE**](https://api2.hooverandstrong.com/pdfs/CreditApplication-2-7-24-Fillable.pdf)
                      to fill out a Net 30 account credit application.
                    </ReactMarkdown>
                  </Typography>
                  <Typography
                    variant="articleSubTitle"
                    align="center"
                    sx={{ paddingLeft: "1rem", paddingRight: "1rem" }}
                  >
                    <ReactMarkdown linkTarget="_blank">
                      Please note certified diamonds and harmony melee
                      availability is not confirmed until your order is
                      processed and you receive a sales order confirmation
                    </ReactMarkdown>
                  </Typography>
                  <Stack
                    spacing={3}
                    justifyContent="flex-end"
                    alignItems="flex-end"
                  >
                    {totals && (
                      <>
                        {(cart || []).map((lineItem) => (
                          <React.Fragment key={lineItem.id}>
                            {(lineItem.invoices || []).map((invoice) => (
                              <Stack
                                key={invoice.specification}
                                justifyContent="flex-end"
                                alignItems="flex-end"
                              >
                                <Typography
                                  variant="articleText"
                                  sx={{ marginBottom: "1rem" }}
                                >
                                  {parse(invoice.specification)} |{" "}
                                  {formatting["removeSpace"](invoice.weight)} |{" "}
                                  {formatting["price2"](invoice.total)}
                                  {lineItem.material === "D" &&
                                  lineItem.finger_size !== "" ? (
                                    <>
                                      <br />
                                      SKU {lineItem.finger_size}
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </Typography>
                                <Typography variant="subtitle2">
                                  Estimated Ship Date:{" "}
                                  {lineItem.ship_info.ship_date}
                                </Typography>
                              </Stack>
                            ))}
                          </React.Fragment>
                        ))}

                        <Divider flexItem />
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Subtotal: {formatting["price2"](totals.subtotal)}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Shipping:{" "}
                          {showShippingPrice ? (
                            formatting["price2"](totals.trans)
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Sales Tax:{" "}
                          {showShippingPrice ? (
                            formatting["price2"](
                              totals.tax && totals.tax.amount
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                        <Typography
                          variant="articleText"
                          sx={{ marginBottom: "1rem" }}
                        >
                          Total:{" "}
                          {showShippingPrice ? (
                            formatting["price2"](totals.total)
                          ) : (
                            <Skeleton
                              variant="text"
                              width="10rem"
                              className="float-right"
                            />
                          )}
                        </Typography>
                      </>
                    )}
                  </Stack>
                  {!submitted && (
                    <Stack
                      direction="row"
                      spacing={3}
                      justifyContent="flex-end"
                      alignItems="flex-end"
                      sx={{ marginTop: "2rem" }}
                    >
                      <DomLink to={{ pathname: "/cart" }}>
                        <Button
                          size="small"
                          color="secondary"
                          variant="contained"
                        >
                          Cancel
                        </Button>
                      </DomLink>
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        onClick={() => checkout()}
                        disabled={!isCheckoutValid}
                      >
                        Place Order
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Default>
        <Dialog
          open={openNewAddressModal}
          onClose={() => setOpenNewAddressModal(false)}
          fullWidth
          scroll="body"
        >
          <>
            <DialogTitle sx={{ textTransform: "lowercase" }}>
              New Shipping Address
            </DialogTitle>
            <DialogContent sx={{ minHeight: "40rem" }} dividers={true}>
              <Stack spacing={1}>
                <Typography sx={{ marginBottom: "2rem" }}>
                  Fill out the fields to add a new address.
                </Typography>
                <Stack direction={"row"} spacing={1}>
                  <Box width={"100%"}>
                    <InputLabel>Name *</InputLabel>
                    <TextField
                      color="primary"
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewAddress",
                          field: "name",
                          payload: e.target.value,
                        })
                      }
                      value={newAddress?.name || ""}
                      size="small"
                      sx={{
                        width: "100%",
                        margin: "unset",
                      }}
                    />
                  </Box>
                  <Box width={"100%"}>
                    <InputLabel>Address Type *</InputLabel>
                    <Select
                      value={newAddress?.address_type || ""}
                      size="small"
                      onChange={(e) => {
                        cartDispatch({
                          type: "setNewAddress",
                          field: "address_type",
                          payload: e.target.value,
                        });
                      }}
                      sx={{ width: "100%" }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            border: "1px solid #939598",
                            borderRadius: "0",
                            overflowY: "auto",
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {(constants.addressType || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Stack>
                <InputLabel>Street 1 *</InputLabel>
                <TextField
                  color="primary"
                  onChange={(e) =>
                    cartDispatch({
                      type: "setNewAddress",
                      field: "street_1",
                      payload: e.target.value,
                    })
                  }
                  value={newAddress?.street_1 || ""}
                  size="small"
                  sx={{
                    width: "100%",
                    margin: "unset",
                  }}
                />
                <InputLabel>Street 2</InputLabel>
                <TextField
                  color="primary"
                  onChange={(e) =>
                    cartDispatch({
                      type: "setNewAddress",
                      field: "street_2",
                      payload: e.target.value,
                    })
                  }
                  value={newAddress?.street_2 || ""}
                  size="small"
                  sx={{
                    width: "100%",
                    margin: "unset",
                  }}
                />
                <Stack direction={"row"} gap={2}>
                  <Box width={"100%"}>
                    <InputLabel>State/Province *</InputLabel>
                    <TextField
                      color="primary"
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewAddress",
                          field: "state",
                          payload: e.target.value,
                        })
                      }
                      value={newAddress?.state || ""}
                      size="small"
                      sx={{
                        width: "100%",
                        margin: "unset",
                      }}
                    />
                  </Box>
                  <Box width={"100%"}>
                    <InputLabel>Country *</InputLabel>
                    <Select
                      value={newAddress?.country_code || ""}
                      size="small"
                      onChange={(e) => {
                        cartDispatch({
                          type: "setNewAddress",
                          field: "country_code",
                          payload: e.target.value,
                        });
                      }}
                      sx={{ width: "100%" }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            border: "1px solid #939598",
                            borderRadius: "0",
                            overflowY: "auto",
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {(constants.countriesOptions || []).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Stack>
                <Stack direction={"row"} gap={2}>
                  <Box width={"100%"}>
                    <InputLabel>City *</InputLabel>
                    <TextField
                      color="primary"
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewAddress",
                          field: "city",
                          payload: e.target.value,
                        })
                      }
                      value={newAddress?.city || ""}
                      size="small"
                      sx={{
                        width: "100%",
                        margin: "unset",
                      }}
                    />
                  </Box>
                  <Box width={"100%"}>
                    <InputLabel>Zip Code *</InputLabel>
                    <TextField
                      color="primary"
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewAddress",
                          field: "zip_code",
                          payload: e.target.value,
                        })
                      }
                      value={newAddress?.zip_code || ""}
                      size="small"
                      sx={{
                        width: "100%",
                        margin: "unset",
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Stack direction="row" gap={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setOpenNewAddressModal(false)}
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => submitNewAddress()}
                  variant="contained"
                  color="success"
                  size="small"
                >
                  Add
                </Button>
              </Stack>
            </DialogActions>
          </>
        </Dialog>
        <Dialog
          open={openNewCardModal}
          onClose={() => setOpenNewCardModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <>
            <DialogTitle
              sx={{ textTransform: "capitalize", fontWeight: "bold" }}
            >
              Add New Credit Card
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    Fill out the fields below to add a new credit card.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel htmlFor="name" shrink>
                    Name *
                  </InputLabel>
                  <Box
                    id="name"
                    component="input"
                    type="text"
                    value={newCreditCard?.Name || ""}
                    onChange={(e) => handleTermsChange(e)}
                    sx={inputBoxStyles}
                  />
                </Grid>
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={8}>
                    <InputLabel htmlFor="spreedly-number-test" shrink>
                      Card Number *
                    </InputLabel>
                    <Box id="spreedly-number-test" sx={iFrameBoxStyles} />
                  </Grid>
                  <Grid item xs={4}>
                    <InputLabel htmlFor="spreedly-cvv-test" shrink>
                      CVV *
                    </InputLabel>
                    <Box id="spreedly-cvv-test" sx={iFrameBoxStyles} />
                  </Grid>
                </Grid>
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <InputLabel htmlFor="exp-month" shrink>
                      Exp Month (MM) *
                    </InputLabel>
                    <Box
                      id="exp-month"
                      component="input"
                      type="text"
                      value={newCreditCard?.ExpirationMonth || ""}
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewCreditCard",
                          field: "ExpirationMonth",
                          payload: e.target.value,
                        })
                      }
                      sx={inputBoxStyles}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} sx={{ marginBottom: "2rem" }}>
                    <InputLabel htmlFor="exp-year" shrink>
                      Exp Year (YYYY) *
                    </InputLabel>
                    <Box
                      id="exp-year"
                      component="input"
                      type="text"
                      value={newCreditCard?.ExpirationYear || ""}
                      onChange={(e) =>
                        cartDispatch({
                          type: "setNewCreditCard",
                          field: "ExpirationYear",
                          payload: e.target.value,
                        })
                      }
                      sx={inputBoxStyles}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Stack direction="row" gap={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setOpenNewCardModal(false)}
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => submitNewCard()}
                  variant="contained"
                  color="success"
                  size="small"
                >
                  Add
                </Button>
              </Stack>
            </DialogActions>
          </>
        </Dialog>
        {!pageLoaded && (
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={true}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </Container>
    </CartContext.Provider>
  );
}
